import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  adminModuleNames,
  // AdminSettingsText,
  iconMapping,
  MenuText,
  UserModuleNavItems,
  StandaloneNavItems,
  GroupedNavItems,
} from './data/constants';
import { hasViewPermission } from '../../services';
import { GrigoLogo, MenuCircleOut } from '../../assets/images';
import StyledTooltip from '../tooltip';
import { NavCollapsedContext } from '../../utils/context';

interface UserModuleNavProps {
  isCollapsed?: boolean;
  onCollapsedChange?: (value: boolean) => void;
}

function UserModuleNav({
  isCollapsed: controlledCollapsed,
  onCollapsedChange,
}: UserModuleNavProps = {}) {
  const navigate = useNavigate();
  const { isNavCollapsed, setIsNavCollapsed } = useContext(NavCollapsedContext);

  const [internalCollapsed, setInternalCollapsed] = useState<boolean>(() => {
    const storedValue = localStorage.getItem('isMenuCollapsed');
    return storedValue ? JSON.parse(storedValue) : false;
  });

  const isCollapsed = onCollapsedChange ? (controlledCollapsed ?? false) : internalCollapsed;
  const setCollapsed = (value: boolean) => {
    if (onCollapsedChange) onCollapsedChange(value);
    else setInternalCollapsed(value);
  };

  const [, setIsAdminExpanded] = useState<boolean>(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [showSettingsPopup, setShowSettingsPopup] = useState<boolean>(false);

  const [, setActiveTab] = useState<number>(() => {
    const storedValue = localStorage.getItem('activeTab');
    return storedValue !== null ? JSON.parse(storedValue) : 0;
  });

  const userPermissions = localStorage.getItem('permissions');
  const parsedPermissions = userPermissions ? JSON.parse(userPermissions) : [];

  const permittedModules = parsedPermissions.map((p: { module: string }) =>
    p.module.toLowerCase().replaceAll(' ', '-'),
  );

  useEffect(() => {
    if (!onCollapsedChange) {
      localStorage.setItem('isMenuCollapsed', JSON.stringify(isCollapsed));
    }
  }, [isCollapsed, onCollapsedChange]);

  // Function to generate navigation paths
  const generatePath = (itemName: string) => {
    if (itemName === 'Assistant') return '/all-assistants';
    return `/${itemName.toLowerCase().replaceAll(' ', '-')}`;
  };

  // Function to check if the current path matches the tab
  const isActiveTab = (tabName: string, alternativePath: string = '') =>
    window.location.pathname.includes(tabName.toLowerCase()) ||
    window.location.pathname.includes(tabName.toLowerCase().replaceAll(' ', '-')) ||
    (alternativePath && window.location.pathname.includes(alternativePath));

  // Filter standalone items
  const filteredStandaloneItems = StandaloneNavItems.filter((item) => {
    let moduleName = item.moduleKey.toLowerCase();
    if (moduleName?.includes(' ')) {
      moduleName = moduleName?.replaceAll(' ', '-');
    }
    return (
      permittedModules.includes(moduleName) && hasViewPermission(moduleName, parsedPermissions)
    );
  });

  // Filter grouped items
  const filteredGroupedItems = Object.entries(GroupedNavItems).reduce(
    (acc, [groupName, group]) => {
      const filteredItems = group.items.filter((item) => {
        let moduleName = item.moduleKey.toLowerCase();
        if (moduleName?.includes(' ')) {
          moduleName = moduleName?.replaceAll(' ', '-');
        }
        return (
          permittedModules.includes(moduleName) && hasViewPermission(moduleName, parsedPermissions)
        );
      });

      if (filteredItems.length > 0) {
        acc[groupName] = { ...group, items: filteredItems };
      }
      return acc;
    },
    {} as Record<string, (typeof GroupedNavItems)[keyof typeof GroupedNavItems]>,
  );

  // Legacy filtering for admin items
  const filteredItems = UserModuleNavItems.filter((item) => {
    let moduleName = item.moduleKey.toLowerCase();
    if (moduleName?.includes(' ')) {
      moduleName = moduleName?.replaceAll(' ', '-');
    }
    return (
      permittedModules.includes(moduleName) && hasViewPermission(moduleName, parsedPermissions)
    );
  });

  const adminItems = filteredItems.filter((item) => {
    const moduleName = item.moduleKey.toLowerCase().replaceAll(' ', '-');
    return adminModuleNames?.includes(moduleName);
  });

  useEffect(() => {
    // Keep admin expanded if any admin item is active
    const hasActiveAdminItem = adminItems.some((item) =>
      isActiveTab(item.tabName, item.tabName === 'Assistant' ? 'all-assistants' : ''),
    );
    if (hasActiveAdminItem) {
      setIsAdminExpanded(true);
    }

    // Keep groups expanded if any item in the group is active
    const newExpandedGroups = { ...expandedGroups };
    Object.entries(filteredGroupedItems).forEach(([groupName, group]) => {
      const hasActiveItem = group.items.some((item) =>
        isActiveTab(item.tabName, item.tabName === 'Assistant' ? 'all-assistants' : ''),
      );
      if (hasActiveItem) {
        newExpandedGroups[groupName] = true;
      }
    });
    setExpandedGroups(newExpandedGroups);
  }, [window.location.pathname]);

  const toggleGroup = (groupName: string) => {
    if (groupName === 'Settings') {
      setShowSettingsPopup(!showSettingsPopup);
    } else {
      setExpandedGroups((prev) => {
        const isCurrentlyExpanded = prev[groupName];
        // If opening a group, close all others (accordion behavior)
        if (!isCurrentlyExpanded) {
          return {
            [groupName]: true,
          };
        }
        // If closing, just close this one
        return {
          ...prev,
          [groupName]: false,
        };
      });
    }
  };

  const handleSettingsItemClick = (itemName: string) => {
    setShowSettingsPopup(false);
    if (isCollapsed) {
      setIsNavCollapsed(false);
      setCollapsed(false);
      if (!onCollapsedChange) {
        localStorage.setItem('isMenuCollapsed', JSON.stringify(false));
      }
    }
    navigate(generatePath(itemName));
  };

  const handleItemClick = (index: number, itemName: string) => {
    if (itemName === 'Chat' && !isCollapsed) {
      setIsNavCollapsed(true);
      setCollapsed(true);
      // Route change unmounts this nav before the isCollapsed → localStorage effect can run.
      // The next page mounts a new UserModuleNav that reads isMenuCollapsed synchronously on init.
      if (!onCollapsedChange) {
        localStorage.setItem('isMenuCollapsed', JSON.stringify(true));
      }
    } else if (itemName !== 'Chat' && isCollapsed) {
      setIsNavCollapsed(false);
      setCollapsed(false);
      if (!onCollapsedChange) {
        localStorage.setItem('isMenuCollapsed', JSON.stringify(false));
      }
    }
    setActiveTab(index);
    localStorage.setItem('activeTab', index.toString());
    navigate(generatePath(itemName));
  };

  return (
    <ul
      className={`flex flex-col h-screen rounded-tr-lg text-[#ffffff] bg-[#151F26] pt-[20px] px-[15px] overflow-y-scroll invisible-scroller transition-all duration-300 ${isCollapsed ? 'w-[85px] min-w-[85px]' : 'w-[270px] min-w-[270px]'}`}
    >
      <div
        className={`flex items-center mb-6 px-2 relative ${
          isCollapsed ? 'justify-center flex-col gap-2' : 'justify-between'
        }`}
      >
        <img
          src={GrigoLogo}
          alt="menu-logo"
          className={`${isCollapsed ? 'h-6 w-auto' : 'h-10 w-auto'}`}
        />
        <span
          role="button"
          tabIndex={0}
          onClick={() => {
            setIsNavCollapsed(!isNavCollapsed);
            setCollapsed(!isCollapsed);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setCollapsed(!isCollapsed);
            }
          }}
          className="cursor-pointer"
        >
          <img
            src={MenuCircleOut}
            alt="menu-circle"
            className={`w-4 h-4 ${isCollapsed ? 'rotate-180' : ''} transition-transform duration-300`}
          />
        </span>
      </div>
      {!isCollapsed && (
        <div className="mb-4 px-2">
          <span className="text-gray-400 text-xs font-medium tracking-wider uppercase">
            {MenuText}
          </span>
        </div>
      )}

      {/* Standalone Items */}
      {filteredStandaloneItems.map((item, index) => (
        <li key={item.tabName} className="mb-1">
          <button
            type="button"
            className={`flex items-center gap-3 px-3 py-2.5 mr-2 w-full rounded-lg font-normal text-base hover:cursor-pointer transition-colors duration-200 ${
              isActiveTab(item.tabName, item.tabName === 'Assistant' ? 'all-assistants' : '')
                ? 'bg-[#F48B00] text-white'
                : 'hover:bg-[#2A3A47] text-gray-400'
            }`}
            onClick={() => handleItemClick(index, item.tabName)}
          >
            {iconMapping[item.icon] && (
              <StyledTooltip title={item.tabName}>
                <img
                  src={
                    isActiveTab(item.tabName, item.tabName === 'Assistant' ? 'all-assistants' : '')
                      ? iconMapping[item.icon].active
                      : iconMapping[item.icon].inactive
                  }
                  alt={item.tabName}
                  className={`w-4 h-4 ${
                    isActiveTab(item.tabName, item.tabName === 'Assistant' ? 'all-assistants' : '')
                      ? 'brightness-0 invert'
                      : ''
                  }`}
                  style={
                    !isActiveTab(item.tabName, item.tabName === 'Assistant' ? 'all-assistants' : '')
                      ? {
                          filter:
                            'brightness(0) saturate(100%) invert(62%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(90%)',
                        }
                      : undefined
                  }
                />
              </StyledTooltip>
            )}
            {!isCollapsed && (
              <span
                className={`text-base font-normal ${
                  isActiveTab(item.tabName, item.tabName === 'Assistant' ? 'all-assistants' : '')
                    ? 'text-white'
                    : 'text-gray-400'
                }`}
              >
                {item.tabName}
              </span>
            )}
          </button>
        </li>
      ))}

      {/* Grouped Items */}
      {Object.entries(filteredGroupedItems).map(([groupName, group]) => {
        const isSettingsGroup = groupName === 'Settings';
        const groupBgColor = 'bg-[#2A3847]'; // Single background color for all groups

        return (
          <li
            key={groupName}
            className={`${isSettingsGroup ? 'mt-auto mb-2 border-t border-gray-600 pt-2' : 'mb-1'}`}
          >
            <div
              className={`transition-all duration-300 ease-in-out ${
                !isSettingsGroup && expandedGroups[groupName]
                  ? `${groupBgColor} rounded-lg p-1 mb-1`
                  : ''
              }`}
            >
              <button
                type="button"
                className="flex items-center gap-3 px-3 py-2.5 mr-2 w-full rounded-lg font-normal text-base hover:cursor-pointer hover:bg-[#2A3A47] text-gray-400 transition-colors duration-200"
                onClick={() => toggleGroup(groupName)}
              >
                {iconMapping[group.icon] && (
                  <StyledTooltip title={groupName}>
                    <img
                      src={iconMapping[group.icon].inactive}
                      alt={groupName}
                      className="w-5 h-5"
                      style={{
                        filter:
                          'brightness(0) saturate(100%) invert(62%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(90%)',
                      }}
                    />
                  </StyledTooltip>
                )}
                {!isCollapsed && (
                  <>
                    <span className="text-base font-normal text-gray-400">{groupName}</span>
                    <span
                      className={`ml-auto transition-transform duration-300 ease-in-out text-base text-gray-400 ${expandedGroups[groupName] ? 'rotate-90' : ''}`}
                    >
                      &#8250;
                    </span>
                  </>
                )}
              </button>
              {!isSettingsGroup && (
                <ul
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isCollapsed ? 'ml-0 mt-1 space-y-1' : 'mx-4 mt-1 mb-2'
                  } ${
                    expandedGroups[groupName]
                      ? 'max-h-[500px] opacity-100'
                      : 'max-h-0 opacity-0 mt-0 pointer-events-none'
                  }`}
                >
                  {group.items.map((item, itemIndex) => (
                    <li key={item.tabName}>
                      <button
                        type="button"
                        className={`flex items-center gap-3 px-3 py-2.5 mr-2 w-full rounded-lg font-normal text-base hover:cursor-pointer transition-colors duration-200 ${
                          isActiveTab(
                            item.tabName,
                            item.tabName === 'Assistant' ? 'all-assistants' : '',
                          )
                            ? 'bg-[#F48B00] text-white'
                            : 'hover:bg-[#2A3A47] text-gray-400'
                        }`}
                        onClick={() =>
                          handleItemClick(filteredStandaloneItems.length + itemIndex, item.tabName)
                        }
                      >
                        {iconMapping[item.icon] && (
                          <StyledTooltip title={item.tabName}>
                            <img
                              src={
                                isActiveTab(
                                  item.tabName,
                                  item.tabName === 'Assistant' ? 'all-assistants' : '',
                                )
                                  ? iconMapping[item.icon].active
                                  : iconMapping[item.icon].inactive
                              }
                              alt={item.tabName}
                              className={`w-4 h-4 ${
                                isActiveTab(
                                  item.tabName,
                                  item.tabName === 'Assistant' ? 'all-assistants' : '',
                                )
                                  ? 'brightness-0 invert'
                                  : ''
                              }`}
                              style={
                                !isActiveTab(
                                  item.tabName,
                                  item.tabName === 'Assistant' ? 'all-assistants' : '',
                                )
                                  ? {
                                      filter:
                                        'brightness(0) saturate(100%) invert(62%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(90%)',
                                    }
                                  : undefined
                              }
                            />
                          </StyledTooltip>
                        )}
                        {!isCollapsed && (
                          <span
                            className={`text-base font-normal ${
                              isActiveTab(
                                item.tabName,
                                item.tabName === 'Assistant' ? 'all-assistants' : '',
                              )
                                ? 'text-white'
                                : 'text-gray-400'
                            }`}
                          >
                            {item.tabName}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {/* Settings Popup */}
              {isSettingsGroup && (
                <div
                  className={`absolute bottom-16 left-4 bg-white rounded-lg shadow-lg border border-gray-200 py-2 w-60 z-50 transition-all duration-300 ease-in-out transform origin-bottom-left ${
                    showSettingsPopup
                      ? 'opacity-100 scale-100 translate-y-0'
                      : 'opacity-0 scale-95 translate-y-2 pointer-events-none'
                  }`}
                >
                  {group.items?.map((item) => (
                    <button
                      key={item.tabName}
                      type="button"
                      className={`flex items-center gap-3 px-4 py-2 w-full text-left transition-colors ${
                        isActiveTab(
                          item.tabName,
                          item.tabName === 'Assistant' ? 'all-assistants' : '',
                        )
                          ? 'bg-[#F48B00] text-white hover:bg-[#e07d00]'
                          : 'hover:bg-orange-100'
                      }`}
                      onClick={() => handleSettingsItemClick(item.tabName)}
                    >
                      {iconMapping[item.icon] && (
                        <img
                          src={
                            isActiveTab(
                              item.tabName,
                              item.tabName === 'Assistant' ? 'all-assistants' : '',
                            )
                              ? iconMapping[item.icon].active
                              : iconMapping[item.icon].inactive
                          }
                          alt={item.tabName}
                          className={`w-4 h-4 ${
                            isActiveTab(
                              item.tabName,
                              item.tabName === 'Assistant' ? 'all-assistants' : '',
                            )
                              ? 'brightness-0 invert'
                              : 'brightness-0'
                          }`}
                        />
                      )}
                      <span
                        className={`text-sm font-normal ${
                          isActiveTab(
                            item.tabName,
                            item.tabName === 'Assistant' ? 'all-assistants' : '',
                          )
                            ? 'text-white'
                            : 'text-gray-700'
                        }`}
                      >
                        {item.tabName}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </li>
        );
      })}
      {/* Overlay to close settings popup */}
      {showSettingsPopup && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-transparent transition-opacity duration-200 opacity-100"
          onClick={() => setShowSettingsPopup(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowSettingsPopup(false);
            }
          }}
          aria-label="Close settings popup"
        />
      )}
    </ul>
  );
}

UserModuleNav.defaultProps = {
  isCollapsed: undefined,
  onCollapsedChange: undefined,
};

export default UserModuleNav;
